import { useCallback, useEffect, useRef, useState } from 'react';

import { migrateFromCsv } from 'api/line';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TCrawlMigrateError, TMigrateFromCsvEntry } from 'types';

type MigratePhase = 'idle' | 'migrating' | 'completed' | 'error';

type MigrateProgress = {
  phase: MigratePhase;
  totalEntries: number;
  processedEntries: number;
  migratedMembers: number;
  skippedMembers: number;
  failedMembers: number;
  errors: TCrawlMigrateError[];
  error?: string;
};

const initialProgress: MigrateProgress = {
  phase: 'idle',
  totalEntries: 0,
  processedEntries: 0,
  migratedMembers: 0,
  skippedMembers: 0,
  failedMembers: 0,
  errors: [],
};

export function useMigrateFromCsv() {
  const { user } = useAuth();
  const strings = lineStrings.management.migration.crawl;
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<MigrateProgress>(initialProgress);
  const abortRef = useRef(false);

  useEffect(() => () => { abortRef.current = true; }, []);

  const start = useCallback(
    async (entries: TMigrateFromCsvEntry[], organizationId?: number) => {
      if (!user) {
        setProgress({ ...initialProgress, phase: 'error', error: strings.errors.unauthorized });
        return;
      }

      const validEntries = entries.filter((e) => e.line_user_id);
      abortRef.current = false;
      setIsRunning(true);
      setProgress({
        ...initialProgress,
        phase: 'migrating',
        totalEntries: validEntries.length,
      });

      try {
        const allErrors: TCrawlMigrateError[] = [];
        let totalMigrated = 0;
        let totalSkipped = 0;
        let totalFailed = 0;

        for (let i = 0; i < validEntries.length; i += 50) {
          if (abortRef.current) break;

          const chunk = validEntries.slice(i, i + 50);
          const result = await migrateFromCsv(chunk, organizationId);
          allErrors.push(...result.errors);
          const nextMigrated = totalMigrated + result.migratedMembers;
          const nextSkipped = totalSkipped + result.skippedMembers;
          const nextFailed = totalFailed + result.failedMembers;
          totalMigrated = nextMigrated;
          totalSkipped = nextSkipped;
          totalFailed = nextFailed;

          setProgress((prev) => ({
            ...prev,
            processedEntries: Math.min(i + 50, validEntries.length),
            migratedMembers: nextMigrated,
            skippedMembers: nextSkipped,
            failedMembers: nextFailed,
            errors: allErrors,
          }));
        }

        if (abortRef.current) return;
        setProgress((prev) => ({ ...prev, phase: 'completed' }));
      } catch (caught) {
        const error = caught instanceof Error ? caught.message : strings.errors.migrateFailed;
        setProgress((prev) => ({ ...prev, phase: 'error', error }));
      } finally {
        setIsRunning(false);
      }
    },
    [user, strings],
  );

  const reset = useCallback(() => {
    abortRef.current = true;
    setIsRunning(false);
    setProgress(initialProgress);
  }, []);

  return { start, isRunning, progress, reset };
}
